import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@emotion/react';
import type { Diet, Allergen } from 'types';
import { themeProp } from '@vmos2/utils';

import { RestrictionIcon } from '../icons';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  allergens?: Allergen[],
  diets?: Diet[],
  mode?: String,
  allergensDisplayMode?: 'icon' | 'text',
  dietsDisplayMode?: 'icon' | 'text',
  component: string,
  isLarge: boolean,
  calories?: number,
};

const kJ = 4.184;

const kcalToKj = kcal => kcal * kJ;

const round = (num, precision) => num && num.toFixed(precision);

export const AllergensDiets = ({
  mode,
  allergens = [],
  diets = [],
  allergensDisplayMode = 'icon',
  dietsDisplayMode = 'icon',
  component,
  isLarge,
  calories,
}: Props) => {
  const { t } = useTranslation('');

  const displayAllergensAsText = allergensDisplayMode === 'text';
  const displayDietsAsText = dietsDisplayMode === 'text';

  const componentTheme = useComponentTheme(theme => theme.components[component], mode);
  const themeMode = isLarge ? componentTheme.mode : 'light';
  const themeValue = useMemo(() => ({ mode: themeMode }), [themeMode]);
  return (
    <ThemeProvider theme={themeValue}>
      {!!calories && (
        <Container data-test="calories-row">
          <Title>{t('nutrition:energyTitle')}</Title>
          <List>
            <ListItem displayMode="text">{`${round(calories)} kcal / ${round(kcalToKj(calories))} kJ`}</ListItem>
          </List>
        </Container>
      )}
      {allergens.length > 0 && (
        <Container data-test="allergens-row">
          <Title>{t('dietAllergies:allergensTitle')}</Title>
          <List>
            {displayAllergensAsText ? (
              <ListItem displayMode={allergensDisplayMode}>
                {allergens.map(({ name }) => t(`dietary:${name}`) || name).join(', ')}
              </ListItem>
            ) : (
              allergens.map(allergen => (
                <ListItem displayMode={allergensDisplayMode} key={allergen.uuid}>
                  <RestrictionIcon
                    name={t(`dietary:${allergen.name}`) || allergen.name}
                    iconName={allergen.name}
                    mode={themeMode}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Container>
      )}
      {diets.length > 0 && (
        <Container data-test="diets-row">
          <Title>{t('dietAllergies:dietsTitle')}</Title>
          <List>
            {displayDietsAsText ? (
              <ListItem displayMode={dietsDisplayMode}>
                {diets.map(({ name }) => t(`dietary:${name}`) || name).join(', ')}
              </ListItem>
            ) : (
              diets.map(diet => (
                <ListItem key={diet.uuid}>
                  <RestrictionIcon
                    name={t(`dietary:${diet.name}`) || diet.name}
                    iconName={diet.name}
                    mode={themeMode}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Container>
      )}
    </ThemeProvider>
  );
};

AllergensDiets.defaultProps = {
  mode: 'dark',
};

const Container = styled('div')`
  display: flex;
  margin: 0 0 24px;
`;

const Title = styled('span')`
  font-size: ${themeProp('fontSize.md')};
  font-weight: ${themeProp('fontWeight.medium')};
  color: ${({ theme }) => (theme.mode === 'dark' ? theme.color.white : theme.color.black)};
  flex: 75px 0 0;
  margin-right: 20px;
  padding-top: 20px;
`;

const List = styled('ul')`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  flex-wrap: wrap;
`;

const ListItem = styled('li')`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ displayMode, theme }) =>
    displayMode !== 'text' &&
    `
    margin: 4px;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: ${theme.fontSize.xl};
    border: 1px solid ${theme.mode === 'dark' ? theme.color.grey : theme.color.lightGrey};
  `}

  ${({ displayMode, theme: { fontSize, color, mode } }) =>
    displayMode === 'text' &&
    `
    padding-top: 20px;
    font-size: ${fontSize.md};
    color: ${mode === 'dark' ? color.white : color.black};
  `}
`;
